import { useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Code2,
  FileCode2,
  Globe,
  Info,
  Loader2,
  RefreshCw,
  Timer,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useNodeManifest } from '@/hooks/queries/workflow/use-node-manifest';
import {
  describeNodeCodeError,
  getCustomCodePin,
  shortSha,
} from '@/pages/workflow/helper/custom-code-manifest';
import type {
  ManifestField,
  ManifestProblem,
  NodeManifestSummary,
} from '@/services/workflow/node-code';
import type { NodeStruct } from '@/types/node-types';

/**
 * The Custom Code (Lambda) node's `node.json`, as a card at the top of the
 * node's config panel: what the script is called and how it runs, the
 * fields it reads and writes, the files and hosts it may touch, and what
 * is wrong with the manifest in plain words. Read-only; "Open source" goes
 * to the editor for changes.
 */

interface CustomCodeScriptCardProps {
  orgId?: string;
  workflowId?: string;
  nodeId: string;
  struct: NodeStruct;
  /** Switch the panel to the Source tab (absent when the editor is unavailable). */
  onOpenSource?: () => void;
}

const SEVERITY_STYLE: Record<ManifestProblem['severity'], string> = {
  error:
    'border-red-500/30 bg-red-500/5 text-red-700 dark:text-red-300 [&>svg]:text-red-600',
  warning:
    'border-amber-500/30 bg-amber-500/5 text-amber-800 dark:text-amber-300 [&>svg]:text-amber-600',
  info: 'border-border bg-muted/40 text-muted-foreground [&>svg]:text-muted-foreground',
};

function ProblemList({ problems }: { problems: ManifestProblem[] }) {
  if (!problems.length) return null;
  return (
    <ul className='space-y-1.5' data-testid='script-card-problems'>
      {problems.map((problem) => (
        <li
          key={problem.code}
          data-severity={problem.severity}
          className={cn(
            'flex gap-2 rounded-md border p-2 text-xs leading-relaxed',
            SEVERITY_STYLE[problem.severity],
          )}
        >
          {problem.severity === 'info' ? (
            <Info className='mt-0.5 size-3.5 shrink-0' />
          ) : (
            <AlertTriangle className='mt-0.5 size-3.5 shrink-0' />
          )}
          <span>{problem.message}</span>
        </li>
      ))}
    </ul>
  );
}

function FieldRows({
  fields,
  depth = 0,
}: {
  fields: ManifestField[];
  depth?: number;
}) {
  return (
    <>
      {fields.map((field) => (
        <FieldRow key={`${depth}-${field.name}`} field={field} depth={depth} />
      ))}
    </>
  );
}

function FieldRow({ field, depth }: { field: ManifestField; depth: number }) {
  return (
    <>
      <tr className='border-t border-border/60 align-top'>
        <td
          className='py-1 pr-2 font-mono text-xs'
          style={{ paddingLeft: depth ? `${depth * 12 + 4}px` : '4px' }}
        >
          {depth > 0 && <span className='text-muted-foreground'>└ </span>}
          {field.name}
          {field.required && (
            <span
              className='ml-1 text-red-600 dark:text-red-400'
              title='Required'
              aria-label='required'
            >
              *
            </span>
          )}
        </td>
        <td className='py-1 pr-2 text-xs text-muted-foreground whitespace-nowrap'>
          {field.type}
        </td>
        <td className='py-1 text-xs text-muted-foreground'>
          {field.description || <span className='opacity-50'>—</span>}
        </td>
      </tr>
      {field.children?.length ? (
        <FieldRows fields={field.children} depth={depth + 1} />
      ) : null}
    </>
  );
}

function FieldTable({
  title,
  fields,
  valueRow,
  emptyText,
  testId,
}: {
  title: string;
  fields: ManifestField[];
  valueRow: ManifestField | null;
  emptyText: string;
  testId: string;
}) {
  const rows = valueRow ? [valueRow] : fields;
  return (
    <section data-testid={testId}>
      <h4 className='mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
        {title}
        <span className='ml-1 font-normal normal-case tracking-normal'>
          ({rows.length})
        </span>
      </h4>
      {rows.length ? (
        <table className='w-full border-collapse text-left'>
          <thead>
            <tr className='text-[11px] text-muted-foreground'>
              <th className='py-0.5 pl-1 pr-2 font-medium'>Field</th>
              <th className='py-0.5 pr-2 font-medium'>Type</th>
              <th className='py-0.5 font-medium'>Description</th>
            </tr>
          </thead>
          <tbody>
            <FieldRows fields={rows} />
          </tbody>
        </table>
      ) : (
        <p className='text-xs text-muted-foreground'>{emptyText}</p>
      )}
    </section>
  );
}

function Facts({ summary }: { summary: NodeManifestSummary }) {
  const facts: Array<{ icon: typeof Timer; label: string; value: string }> = [];
  facts.push({
    icon: Timer,
    label: 'Timeout',
    value: summary.timeout_seconds
      ? `${summary.timeout_seconds} s`
      : 'node default',
  });
  facts.push({
    icon: Globe,
    label: 'Network',
    value: summary.network_isolated
      ? 'off (no internet)'
      : `on: ${summary.allowed_hosts.join(', ')}`,
  });
  if (summary.input_files) {
    const f = summary.input_files;
    facts.push({
      icon: FileCode2,
      label: 'Files in',
      value: [
        f.required ? 'required' : 'optional',
        f.max_count ? `up to ${f.max_count}` : null,
        f.patterns.length ? f.patterns.join(', ') : null,
      ]
        .filter(Boolean)
        .join(', '),
    });
  } else {
    facts.push({
      icon: FileCode2,
      label: 'Files in',
      value: 'any upstream files',
    });
  }
  facts.push({
    icon: FileCode2,
    label: 'Files out',
    value: summary.output_files?.max_count
      ? `up to ${summary.output_files.max_count}`
      : 'platform cap',
  });
  if (summary.install_timeout_seconds) {
    facts.push({
      icon: Timer,
      label: 'npm install',
      value: `up to ${summary.install_timeout_seconds} s`,
    });
  }
  return (
    <dl
      className='grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs'
      data-testid='script-card-facts'
    >
      {facts.map((fact) => (
        <div key={fact.label} className='contents'>
          <dt className='flex items-center gap-1 text-muted-foreground'>
            <fact.icon className='size-3' />
            {fact.label}
          </dt>
          <dd className='truncate' title={fact.value}>
            {fact.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function CustomCodeScriptCard({
  orgId,
  workflowId,
  nodeId,
  struct,
  onOpenSource,
}: CustomCodeScriptCardProps) {
  const pin = getCustomCodePin(struct);
  const persisted = Boolean(orgId && workflowId && workflowId !== 'new');
  const query = useNodeManifest(
    orgId,
    workflowId,
    nodeId,
    pin.scriptPath,
    pin.scriptSha,
    persisted && pin.pinned,
  );
  const [showJson, setShowJson] = useState(false);

  let body: React.ReactNode;
  let headline: React.ReactNode = 'Script';
  let runtime: string | null = null;

  if (!pin.pinned) {
    body = (
      <p
        className='text-xs text-muted-foreground'
        data-testid='script-card-unpinned'
      >
        No script is pinned yet. Enter the script path and the commit SHA below
        {onOpenSource ? ', or open Source to create one' : ''}. The card then
        shows what the script reads and writes.
      </p>
    );
  } else if (!persisted) {
    body = (
      <p
        className='text-xs text-muted-foreground'
        data-testid='script-card-unsaved'
      >
        Save the workflow to read <span className='font-mono'>node.json</span>{' '}
        from the repository at the pinned commit.
      </p>
    );
  } else if (query.isPending) {
    body = (
      <p
        className='flex items-center gap-2 text-xs text-muted-foreground'
        data-testid='script-card-loading'
      >
        <Loader2 className='size-3.5 animate-spin' />
        Reading node.json at {shortSha(pin.scriptSha)}…
      </p>
    );
  } else if (query.isError) {
    body = (
      <div className='space-y-2' data-testid='script-card-error'>
        <p className='flex gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-xs text-amber-800 dark:text-amber-300'>
          <AlertTriangle className='mt-0.5 size-3.5 shrink-0 text-amber-600 dark:text-amber-400' />
          {describeNodeCodeError(query.error)}
        </p>
        <Button
          size='sm'
          variant='outline'
          onClick={() => void query.refetch()}
          className='h-7 text-xs'
        >
          <RefreshCw className='mr-1 size-3' /> Try again
        </Button>
      </div>
    );
  } else if (!query.data?.summary) {
    body = (
      <p
        className='text-xs text-muted-foreground'
        data-testid='script-card-unpinned'
      >
        No script is pinned yet.
      </p>
    );
  } else {
    const summary = query.data.summary;
    runtime = summary.runtime;
    headline = summary.valid ? summary.name : 'Script';
    const errors = summary.problems.filter((p) => p.severity === 'error');
    const rest = summary.problems.filter((p) => p.severity !== 'error');
    body = (
      <div className='space-y-3' data-testid='script-card-body'>
        {summary.valid && summary.description ? (
          <p className='text-xs text-muted-foreground'>{summary.description}</p>
        ) : null}
        <ProblemList problems={errors} />
        {summary.valid && (
          <>
            <FieldTable
              title='Inputs'
              testId='script-card-inputs'
              fields={summary.inputs}
              valueRow={summary.input_value}
              emptyText={
                summary.input_schema
                  ? 'input_schema names no fields.'
                  : 'No input_schema declared: the script accepts any JSON.'
              }
            />
            <FieldTable
              title='Outputs'
              testId='script-card-outputs'
              fields={summary.outputs}
              valueRow={summary.output_value}
              emptyText={
                summary.output_schema
                  ? 'output_schema names no fields.'
                  : 'No output_schema declared: downstream nodes see whatever the script writes.'
              }
            />
            <Facts summary={summary} />
          </>
        )}
        <ProblemList problems={rest} />
        {summary.raw && (
          <div>
            <button
              type='button'
              className='flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground'
              onClick={() => setShowJson((open) => !open)}
              aria-expanded={showJson}
              data-testid='script-card-json-toggle'
            >
              {showJson ? (
                <ChevronDown className='size-3.5' />
              ) : (
                <ChevronRight className='size-3.5' />
              )}
              {showJson ? 'Hide node.json' : 'View node.json'}
            </button>
            {showJson && (
              <pre
                className='mt-1 max-h-72 overflow-auto rounded-md border bg-muted/30 p-2 font-mono text-[11px] leading-snug'
                data-testid='script-card-json'
              >
                {JSON.stringify(summary.raw, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    );
  }

  const versionLabel =
    query.data?.workflowVersionNumber != null
      ? `v${query.data.workflowVersionNumber}`
      : null;

  return (
    <section
      className='mx-3 mb-4 rounded-lg border bg-card p-3 shadow-sm'
      data-testid='custom-code-script-card'
      aria-label='Script'
    >
      <header className='mb-2 flex flex-wrap items-start justify-between gap-2'>
        <div className='min-w-0'>
          <div className='flex flex-wrap items-center gap-1.5'>
            <Code2 className='size-4 shrink-0 text-muted-foreground' />
            <h3
              className='truncate text-sm font-semibold'
              data-testid='script-card-name'
            >
              {headline}
            </h3>
            {runtime && (
              <span
                className='rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary'
                data-testid='script-card-runtime'
              >
                {runtime}
              </span>
            )}
          </div>
          {pin.pinned && (
            <p
              className='mt-0.5 truncate font-mono text-[11px] text-muted-foreground'
              title={`${pin.scriptPath} @ ${pin.scriptSha}`}
              data-testid='script-card-pin'
            >
              {pin.scriptPath}
              {query.data?.summary?.entrypoint
                ? `/${query.data.summary.entrypoint}`
                : ''}{' '}
              @ {shortSha(query.data?.resolvedSha || pin.scriptSha)}
              {versionLabel ? ` · workflow ${versionLabel}` : ''}
            </p>
          )}
        </div>
        {onOpenSource && persisted && (
          <Button
            size='sm'
            variant='outline'
            className='h-7 shrink-0 text-xs'
            onClick={onOpenSource}
            data-testid='script-card-open-source'
          >
            <FileCode2 className='mr-1 size-3' />
            {pin.pinned ? 'Open source' : 'Create script'}
          </Button>
        )}
      </header>
      {body}
    </section>
  );
}
